<template>
  <div class="my-10 flex flex-col gap-5">
    <div class="">
      <div v-if="bannersLoaded && bannerItems.length">
        <Splide
          @splide:move="stopVideo"
          ref="splide"
          class="w-full"
          :options="{
            slideFocus: true,
            type: 'loop',
            autoplay: false,
            pagination: false,
            pauseOnHover: false,
          }"
          :has-track="false"
          aria-label="similars"
        >
          <SplideTrack class="py-4">
            <SplideSlide v-for="el in bannerItems" :key="keyvideo[`video${el.id}`] || el.id">
              <video
                controlsList="nodownload"
                v-if="el?.path && isVideo(el.path)"
                :ref="'video' + el.id"
                controls
                :src="el.path"
                class="object-contain h-[450px] w-full"
              ></video>
              <img
                v-else
                class="rounded-t h-[450px] w-full object-fill"
                :src="el?.path || detail?.thumbnail || '/placeholder.png'"
                alt="store-banner"
              />
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>

      <div class="w-full flex flex-col gap-5 bg-white p-3 rounded-b">
        <div class="flex items-center gap-3 flex-wrap">
          <h1 class="font-bold text-2xl text-main-color">{{ detail?.name }}</h1>

          <div class="flex items-center gap-2">
            <IconEye></IconEye>
            <span class="font-bold text-main-color text-sm">{{ detail?.view_count }}</span>
          </div>

          <VueStarRating
            :rounded-corners="true"
            :star-size="15"
            active-color="#ffb300"
            inactive-color="#f2f2f2"
            :border-width="3"
            border-color="#ffb300"
            :show-rating="false"
            :read-only="true"
            :increment="0.5"
            :rating="detail?.rating"
          ></VueStarRating>

          <div
            v-if="detail?.is_vip"
            class="px-8 py-2.5 bg-[#7951F9] rounded-lg text-white font-bold text-end max-md:py-2 max-md:px-4"
          >
            VIP
          </div>

          <div
            v-if="detail?.subscription !== ''"
            class="w-max rounded-lg py-2.5 px-8 font-bold max-md:py-2 max-md:px-4"
            :class="getSubcriptionType?.color"
          >
            {{ getSubcriptionType?.name }}
          </div>

          <div class="flex items-center gap-5 ml-auto">
            <router-link
              v-if="detail?.status !== 2"
              :to="{ name: 'updateMyBusinessAccount', params: { id: currentId } }"
              class="bg-[#205bff] text-base rounded-lg py-2.5 px-8 text-white font-semibold"
            >
              {{ $t('nav.updateBusinessAccountInformation') }}
            </router-link>
            <button
              type="button"
              class="bg-red-600 text-white text-base rounded-lg py-2.5 px-8 font-semibold hover:opacity-90"
              @click="deleteRequest"
            >
              {{ $t('page.remove') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-16 bg-white p-8 rounded-b max-md:grid-cols-1 max-md:gap-5">
      <div class="w-full">
        <div class="flex justify-between items-center text-main-color mb-3">
          <p class="text-3xl font-bold">{{ detail?.name }}</p>
          <div class="flex items-center gap-2">
            <IconEye></IconEye>
            <span class="font-bold text-sm">{{ detail?.view_count }}</span>
          </div>
        </div>

        <div class="text-[#333333] text-base pb-4 border-b border-[#E3E3E3]">
          <p class="font-light">{{ detail?.business_type }}</p>
        </div>

        <div class="pt-4 pb-4 border-b border-[#E3E3E3]">
          <p v-if="requestStatusMeta" class="font-bold mb-3">
            {{ $t('page.status') }}:
            <span :class="requestStatusMeta.class">
              {{ requestStatusMeta.label }}
            </span>
          </p>

          <p v-if="formattedRejectionReason" class="font-bold mb-0">
            {{ $t('page.reason') }}:
            <span class="text-red-500 font-medium">{{ formattedRejectionReason }}</span>
          </p>
        </div>

        <div
          v-if="detail?.caption"
          class="text-[#333333] text-base pt-4 pb-4 border-b border-[#E3E3E3]"
        >
          <p class="font-semibold text-main-color mb-1">{{ $t('page.caption') }}</p>
          <p class="font-light">{{ detail?.caption }}</p>
        </div>

        <div class="mt-3">
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.category') }}: <span class="font-semibold">{{ detail?.category }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.jobType') }}:
            <span class="font-semibold">{{ detail?.business_type }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.location') }}: <span class="font-semibold">{{ detail?.location }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.address') }}: <span class="font-semibold">{{ detail?.address }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('form.phone') }}: <span class="font-semibold">{{ detail?.phone }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.accountDate') }}:
            <span class="font-semibold">{{ formatDateTime(detail?.date_started) }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.accountEndDate') }}:
            <span class="font-semibold">{{ formatDateTime(detail?.date_finished) }}</span>
          </p>
          <p v-if="detail?.link" class="tetx-lg text-main-color mb-3">
            {{ $t('page.website') }}:
            <span class="font-semibold">
              <a
                class="!text-accent-color"
                :href="detail.link.startsWith('http') ? detail.link : `https://${detail.link}`"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ detail.link }}
              </a>
            </span>
          </p>
          <p v-if="detail?.subscription" class="tetx-lg text-main-color mb-3">
            "{{ detail.subscription }}" {{ $t('page.subscriptionEndDate') }}:
            <span class="font-semibold">{{
              formatDateTime(detail?.subscription_date_finished)
            }}</span>
          </p>
          <p v-if="detail?.is_vip" class="tetx-lg text-main-color mb-3">
            {{ $t('page.vipEndDate') }}:
            <span class="font-semibold">{{ formatDateTime(detail?.vip_date_finished) }}</span>
          </p>
        </div>
      </div>

      <div class="w-full">
        <div class="accordion">
          <Accordion :label="$t('page.detail')">
            <p>{{ detail?.description }}</p>
          </Accordion>

          <Accordion v-if="detail?.work_hours" :label="$t('page.workingHours')">
            <div class="flex flex-col gap-2">
              <p>
                <span class="font-semibold">{{ $t('page.monday') }}: </span>
                {{ detail?.work_hours['monday'] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.tuesday') }}: </span>
                {{ detail?.work_hours['tuesday'] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.wednesday') }}: </span>
                {{ detail?.work_hours['wednesday'] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.thursday') }}: </span>
                {{ detail?.work_hours['thursday'] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.friday') }}: </span>
                {{ detail?.work_hours['friday'] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.saturday') }}: </span>
                {{ detail?.work_hours['saturday'] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.sunday') }}: </span>
                {{ detail?.work_hours['sunday'] }}
              </p>
            </div>
          </Accordion>

          <Accordion :label="$t('page.phoneNumbers')">
            <div class="flex flex-col gap-2">
              <a
                v-for="(el, index) in detail?.phone_numbers"
                :key="index"
                :href="`tel:${el.phone}`"
              >
                {{ el.phone }}
              </a>
            </div>
          </Accordion>

          <Accordion v-if="detail?.contacts" :label="$t('page.contactsSocialMedia')">
            <div class="flex gap-2 flex-col">
              <a v-for="(el, index) in detail?.contacts" :key="index" :href="el.value">
                {{ CONTACT_TYPE_OPTIONS[el.type] }}: {{ el.value }}
              </a>
            </div>
          </Accordion>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import Accordion from '@cmp/Accordion.vue';
import IconEye from '@icons/IconEye.vue';
import VueStarRating from 'vue-star-rating';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { isVideo } from '@/helpers';
import { CONTACT_TYPE_OPTIONS } from '@/constants';
import { i18n } from '@/i18n';

export default {
  name: 'viewMyBusinessAccountRequest',
  components: {
    Accordion,
    IconEye,
    VueStarRating,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  data() {
    return {
      loading: false,
      detail: null,
      keyvideo: {},
      bannersLoaded: false,
      CONTACT_TYPE_OPTIONS: CONTACT_TYPE_OPTIONS,
      currentId: null,
    };
  },

  methods: {
    isVideo,

    formatDateTime(value) {
      if (!value) return '';

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },

    async getBusinessDetail() {
      this.loading = true;
      try {
        const response = await api.get(`posts/my-business_account-requests/${this.currentId}/`);
        this.detail = response.data;

        (this.detail?.banners || []).forEach(element => {
          if (element?.path && this.isVideo(element.path)) {
            const v = 'video' + element.id;
            this.keyvideo[v] = 1;
          }
        });

        this.bannersLoaded = true;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async deleteRequest() {
      const confirmed = window.confirm(this.$t('page.remove') + '?');
      if (!confirmed) return;

      this.loading = true;
      try {
        await api.delete(`posts/my-business_account-requests/${this.currentId}/`);
        this.$toast.success(i18n.global.t('warning.ACCOUNT_DELETED'));
        this.$router.push({ name: 'myBusinessAccountsRequests' });
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    stopVideo() {
      (this.detail?.banners || []).forEach(element => {
        if (element?.path && this.isVideo(element.path)) {
          const v = 'video' + element.id;
          this.keyvideo[v] = (this.keyvideo[v] || 0) + 1;
        }
      });
    },
  },

  computed: {
    formattedRejectionReason() {
      const html = this.rejectionReasonText || '';
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return (temp.textContent || temp.innerText || '').trim();
    },

    rejectionReasonText() {
      return this.detail?.standard_rejection_reason || this.detail?.rejection_reason || '';
    },

    bannerItems() {
      const banners = this.detail?.banners || [];
      if (banners.length) return banners;

      if (this.detail?.thumbnail) {
        return [
          {
            id: 'thumbnail-fallback',
            path: this.detail.thumbnail,
          },
        ];
      }

      return [
        {
          id: 'placeholder-fallback',
          path: '/placeholder.png',
        },
      ];
    },

    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          return null;
      }
    },

    requestStatusMeta() {
      const locale = this.$i18n?.locale || 'ru';

      if (this.rejectionReasonText) {
        return {
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
          class: 'text-red-500',
        };
      }

      const rawStatus =
        this.detail?.status_code ??
        this.detail?.status ??
        this.detail?.status_value ??
        this.detail?.request_status;

      const rawLabel =
        this.detail?.status_display ??
        this.detail?.status_label ??
        this.detail?.request_status_display ??
        this.detail?.status_name ??
        '';

      const normalizedLabel = String(rawLabel).trim().toLowerCase();

      if (
        rawStatus === 0 ||
        rawStatus === '0' ||
        rawStatus === 1 ||
        rawStatus === '1' ||
        rawStatus === 'pending' ||
        rawStatus === 'waiting' ||
        normalizedLabel.includes('ожида') ||
        normalizedLabel.includes('garaş')
      ) {
        return {
          label: locale === 'tk' ? 'Garaşylýar' : 'Ожидается',
          class: 'text-yellow-500',
        };
      }

      if (
        rawStatus === 3 ||
        rawStatus === '3' ||
        rawStatus === 'rejected' ||
        rawStatus === 'declined' ||
        normalizedLabel.includes('отклон') ||
        normalizedLabel.includes('ret edildi')
      ) {
        return {
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
          class: 'text-red-500',
        };
      }

      return {
        label: locale === 'tk' ? 'Garaşylýar' : 'Ожидается',
        class: 'text-yellow-500',
      };
    },
  },

  created() {
    this.currentId = this.$route.params.id;
    this.getBusinessDetail();
  },
};
</script>
